import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {AlertService} from '../services/alert.service';
import {EnvService} from '../services/env.service';
import { AuthService } from '../services/auth.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'micro-cmdb-cluster-detail',
  templateUrl: './cmdb-cluster-detail.component.html'
})
export class CmdbClusterDetailComponent implements OnInit {
  @Input() cmdbClusterId: any;
  @Output() persisted = new EventEmitter<any>();
  cmdbCluster: any;
  selectedPlatform: any;
  validatingCompany = false;
  companyValidationFailed = false;
  private companyValidation?: Subscription;
  private subscriptions = new Subscription();

  constructor(public auth: AuthService, private env: EnvService, private http: HttpClient, private route: ActivatedRoute,
              private router: Router, private alertService: AlertService) {
  }

  ngOnInit(): void {
    if (this.cmdbClusterId) {
      this.load(this.cmdbClusterId);

    } else {
      this.subscriptions.add(this.route.params.subscribe((params: Params) => {
        this.selectedPlatform = undefined;
        params['id'] === '0' ? this.createNew() : this.load(params['id']);
      }));
    }
  }

  load(id: string): void {
    this.companyValidation?.unsubscribe();
    this.selectedPlatform = undefined;
    this.subscriptions.add(this.http.get(`${this.env.e.url}/domain/cmdbClusters/${id}`).subscribe(data => {
      this.cmdbCluster = data;
      this.validateCompanySelections();
    }));
  }

  createNew(): void {
    this.companyValidation?.unsubscribe();
    this.selectedPlatform = undefined;
    this.validatingCompany = false;
    this.companyValidationFailed = false;
    this.cmdbCluster = { name: '', platformId: '', description: 'None', active: 'N', companyId: this.auth.getCompanyId() };
  }

  save(): void {
    if (this.validatingCompany || this.companyValidationFailed || !this.cmdbCluster.companyId || !this.cmdbCluster.platformId) {
      return;
    }
    const params = new HttpParams().set('companyId', this.cmdbCluster.companyId || this.auth.getCompanyId());
    const isUpdate = !!this.cmdbCluster.id;
    const request = isUpdate
      ? this.http.put(`${this.env.e.url}/domain/cmdbClusters/${this.cmdbCluster.id}`, this.cmdbCluster, {params})
      : this.http.post(`${this.env.e.url}/domain/cmdbClusters`, this.cmdbCluster, {params});
    this.subscriptions.add(request.subscribe(data => {
      this.cmdbCluster = data;
      this.alertService.info(`${isUpdate ? 'Updated' : 'Created'} ${this.cmdbCluster.name}`);
      this.persisted.emit({data, action: isUpdate ? 'updated' : 'added'});
    }));
  }

  delete(): void {
    this.subscriptions.add(this.http.delete(`${this.env.e.url}/domain/cmdbClusters/${this.cmdbCluster.id}`).subscribe(() => {
      this.persisted.emit({data: this.cmdbCluster, action: 'deleted'});
      this.alertService.info(`Deleted ${this.cmdbCluster.name}`);
      if (!this.cmdbClusterId) { this.router.navigate(['/cmdbClusters']); }
    }));
  }

  ngOnDestroy(): void {
    this.companyValidation?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  onCompanyChange(companyId: any): void {
    if (companyId === this.cmdbCluster.companyId) {
      return;
    }
    this.cmdbCluster.companyId = companyId;
    this.validateCompanySelections();
  }

  validateCompanySelections(): void {
    this.companyValidation?.unsubscribe();
    this.companyValidationFailed = false;
    this.validatingCompany = false;
    const cluster = this.cmdbCluster;
    const companyId = cluster.companyId;
    if (!cluster.platformId) {
      this.selectedPlatform = undefined;
      return;
    }
    if (!companyId) {
      return;
    }

    // Check the selected record's ownership, independently of dropdown pages.
    this.validatingCompany = true;
    this.companyValidation = this.http.get<any>(`${this.env.e.url}/domain/cmdbHostingPlatform/${cluster.platformId}`)
      .subscribe({
        next: platform => {
          if (this.cmdbCluster !== cluster || cluster.companyId !== companyId) {
            return;
          }
          if (platform && platform.companyId !== companyId) {
            cluster.platformId = '';
          }
          this.selectedPlatform = cluster.platformId ? platform : undefined;
          // A missing record does not establish a company mismatch.
          this.companyValidationFailed = !!(cluster.platformId && !platform);
          this.validatingCompany = false;
        },
        error: () => {
          this.validatingCompany = false;
          this.companyValidationFailed = true;
          this.alertService.warn('Unable to validate the selected platform for this company. Please retry.');
        }
      });
  }

  onCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.cmdbCluster.active = target.checked ? 'Y' : 'N';
  }
}
